/* eslint-disable max-lines-per-function */
import { CellClickedEvent, ValueGetterParams } from '@ag-grid-community/core';
import moment from 'moment';

import {
	AddToCartHeaderComponent
} from '../../../../offer/components/utils/product-table/header-def/add-to-cart-header.component';
import { Batch } from '../../../../order/models/batch';
import { ECALL_BY_BATCH_DATACENTER_TYPE, UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { TranslationService } from '../../../services/translation.service';
import {
	CommonColDef, IconMethodColDef, NumericColDef
} from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterNumberFilterComponent } from '../../filter-type/datacenter-number-filter.component';
import { DatacenterOrderNumberFilterComponent } from '../../filter-type/datacenter-order-number-filter.component';
import { DatacenterSuggestiveListFilterComponent } from '../../filter-type/datacenter-suggestive-list-filter.component';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { CheckboxHeaderComponent } from '../../header-components/checkbox-header.component';
import { getFormatDate, getParseDate } from '../../../utils/resolved-format-date';
import { ORDER_SERVER_URL } from '../../../../config';
import { DatacenterDateFilterComponent } from '../../filter-type/datacenter-date-filter.component';
import { DatacenterDropdownFilterComponent } from '../../filter-type/datacenter-dropdown-filter.component';

const GET_CALLABLE_BATCHES_URL = `${ ORDER_SERVER_URL }/v5/datacenterBatches`;

export class EcallByBatchDatacenterColumnDefinition {
	readonly REQUIRED_FIELDS = [
		'id',
		'form',
		'mill',
		'millBatchReference',
		'millDescription',
		'customerOrderNumber',
		'customerArticleReference',
		'batchReference',
		'qtyBatchGross',
		'qtyBatchNet',
		'daysInStock',
		'shipTo',
		'shipToDescription',
		'supplierOrderNumber',
		'supplierOrderItemNumber',
	];

	readonly DEFAULT_CONFIGURATION: Map<string, CommonColDef<Batch>>;

	constructor(
		private readonly translationService: TranslationService
	) {
		this.DEFAULT_CONFIGURATION = new Map<string, CommonColDef<Batch>>([
			[
				'add',
				new CommonColDef<Batch>({
					colId: 'add',
					filter: false,
					isMandatoryOnLeft: true,
					sortable: false,
					headerName: this.translationService.getTR('product.table.header.add'),
					minWidth: 40,
					maxWidth: 40,
					width: 40,
					headerComponentParams: {
						showCheckbox: true,
						icon_checked: 'add_to_cart',
						icon_unchecked: 'add_to_cart_blue',
						icon_indeterminate: 'add_to_cart_grey'
					},
					headerComponent: AddToCartHeaderComponent,
					checkboxSelection: true,
					pinned: 'left'
				})
			],
			[
				'batchReference',
				new CommonColDef<Batch>({
					colId: 'batchReference',
					filter: DatacenterTextFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('shipment.filter.batch-number'),
					headerComponent: CheckboxHeaderComponent,
					field: 'batchReference',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'customerArticleReference',
				new CommonColDef<Batch>({
					colId: 'customerArticleReference',
					filter: DatacenterSuggestiveListFilterComponent,
					filterParams: {
						batchFields: ['customerArticleReference', 'customerArticleReference'],
						url: GET_CALLABLE_BATCHES_URL
					},
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.customer-article-reference'),
					headerComponent: CheckboxHeaderComponent,
					field: 'customerArticleReference',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.customerArticleReference;
					}
				})
			],
			[
				'currentLocation',
				new CommonColDef<Batch>({
					colId: 'currentLocation',
					filter: DatacenterTextFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.current-location'),
					headerComponent: CheckboxHeaderComponent,
					field: 'currentLocationDescription',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'coating',
				new CommonColDef<Batch>({
					colId: 'coating',
					field: 'coating',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						batchFields: ['coating', 'coatingDescription'],
						url: GET_CALLABLE_BATCHES_URL
					},
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.coating'),
					headerComponent: CheckboxHeaderComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.coatingDescription;
					},
				})
			],
			[
				'deliveryMode',
				new CommonColDef<Batch>({
					colId: 'deliveryMode',
					field: 'deliveryMode',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						batchFields: ['deliveryMode', 'deliveryModeDescription'],
						url: GET_CALLABLE_BATCHES_URL
					},
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('filter.deliveryMode'),
					headerComponent: CheckboxHeaderComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.deliveryModeDescription;
					},
				})
			],
			[
				'shipToAddress',
				new CommonColDef<Batch>({
					colId: 'shipToAddress',
					filter: DatacenterTextFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('eoffer.wizard.delivery.address'),
					headerComponent: CheckboxHeaderComponent,
					field: 'shipToAddress',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'serviceLevel',
				new CommonColDef<Batch>({
					colId: 'serviceLevel',
					field: 'serviceLevel',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						batchFields: ['serviceLevel', 'serviceLevelDescription'],
						url: GET_CALLABLE_BATCHES_URL
					},
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.service-level'),
					headerComponent: CheckboxHeaderComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.serviceLevelDescription;
					},
				})
			],
			[
				'colorTopSide',
				new CommonColDef<Batch>({
					colId: 'colorTopSide',
					field: 'colorTopSide',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						batchFields: ['colorTopSide', 'colorTopSideDescription'],
						url: GET_CALLABLE_BATCHES_URL
					},
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('eoffer.table.header.colTopSide'),
					headerComponent: CheckboxHeaderComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.colorTopSideDescription;
					},
				})
			],
			[
				'colorBottomSide',
				new CommonColDef<Batch>({
					colId: 'colorBottomSide',
					field: 'colorBottomSide',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						batchFields: ['colorBottomSide', 'colorBottomSideDescription'],
						url: GET_CALLABLE_BATCHES_URL
					},
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.color-bottom-side'),
					headerComponent: CheckboxHeaderComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.colorBottomSideDescription;
					},
				})
			],
			[
				'shipTo',
				new CommonColDef<Batch>({
					colId: 'shipTo',
					filter: DatacenterTextFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.ship-to'),
					headerComponent: CheckboxHeaderComponent,
					field: 'shipToDescription',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'packageSerialNumber',
				new CommonColDef<Batch>({
					colId: 'packageSerialNumber',
					filter: DatacenterTextFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.package-serial-number'),
					headerComponent: CheckboxHeaderComponent,
					field: 'packageSerialNumber',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'heatNumber',
				new CommonColDef<Batch>({
					colId: 'heatNumber',
					filter: DatacenterTextFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.heat-number'),
					headerComponent: CheckboxHeaderComponent,
					field: 'heatNumber',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'cdd',
				new CommonColDef<Batch>({
					colId: 'cdd',
					field: 'cddWeek',
					filter: DatacenterDateFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					columnType: ColumnType.DATE_COLUMN,
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('filter.cdd'),
					cellRendererParams: { inputFormat: 'isoWeek', outputFormat: 'week' },
					suppressHeaderMenuButton: false,
					valueFormatter: params => {
						const value = <string | undefined> params.value;
						return value ? moment(value, getParseDate('isoWeek')).format(getFormatDate('week')) : '';
					},
					valueGetter: (params: ValueGetterParams<Batch>) => {
						if (!params.data?.cddWeek || params.data.cddWeek.includes('1900-W01')) {
							return '';
						}
						return params.data.cddWeek;
					}
				})
			],
			[
				'soldTo',
				new CommonColDef<Batch>({
					colId: 'soldTo',
					field: 'soldToDescription',
					filter: DatacenterSuggestiveListFilterComponent,
					filterParams: {
						suppressAndOrCondition: true,
						batchFields: ['soldTo', 'soldToDescription'],
						url: GET_CALLABLE_BATCHES_URL
					},
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.sold-to'),
					headerComponent: CheckboxHeaderComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => params.data?.soldToDescription ?? ''
				})
			],
			[
				'customerOrderNumber',
				new CommonColDef<Batch>({
					colId: 'customerOrderNumber',
					fields: ['customerOrderNumber', 'customerOrderItemNumber'],
					filter: DatacenterOrderNumberFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					columnType: ColumnType.ORDER_NUMBER_COLUMN,
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('filter.customerOrderNumber'),
					suppressHeaderMenuButton: false,
					valueGetter: (params: ValueGetterParams<Batch>) =>
						`${ params.data?.customerOrderNumber ?? '' }/${ params.data?.customerOrderItemNumber ?? '' }`
				})
			],
			[
				'daysInStock',
				new CommonColDef<Batch>({
					colId: 'daysInStock',
					filter: DatacenterNumberFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter.nb-days-in-stock'),
					headerComponent: CheckboxHeaderComponent,
					field: 'daysInStock',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'display',
				new IconMethodColDef<Batch>({
					colId: 'display',
					filter: false,
					isMandatoryOnLeft: true,
					sortable: false,
					headerName: this.translationService.getTR('product.table.header.view'),
					maxWidth: 64,
					minWidth: 64,
					width: 64,
					headerComponent: CheckboxHeaderComponent,
					cellRendererParams: {
						icon: 'icon_view'
					},
					pinned: 'left',
					onCellClicked: (event: CellClickedEvent<Batch>) => {
						const context = <{ onDisplay?: (item: Batch) => void } | undefined> event.context;
						if (!context?.onDisplay || !event.node.data) {
							return;
						}
						context.onDisplay(event.node.data);
					}
				})
			],
			[
				'length',
				new CommonColDef<Batch>({
					colId: 'length',
					filter: DatacenterNumberFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('batch-card.length'),
					headerComponent: CheckboxHeaderComponent,
					field: 'length',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'millDescription',
				new CommonColDef<Batch>({
					colId: 'millDescription',
					filter: DatacenterTextFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN,
					field: 'millDescription',
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('datacenter-filter.mill'),
					suppressHeaderMenuButton: false
				})
			],
			[
				'qtyBatchGross',
				new NumericColDef<Batch>({
					colId: 'qtyBatchGross',
					filter: DatacenterNumberFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter.qty-gross'),
					headerComponent: CheckboxHeaderComponent,
					field: 'qtyBatchGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'qtyBatchNet',
				new NumericColDef<Batch>({
					colId: 'qtyBatchNet',
					filter: DatacenterNumberFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter.qty-net'),
					headerComponent: CheckboxHeaderComponent,
					field: 'qtyBatchNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'producedLength',
				new NumericColDef<Batch>({
					colId: 'producedLength',
					filter: DatacenterNumberFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.producedLength'),
					headerComponent: CheckboxHeaderComponent,
					field: 'producedLength',
					cellRendererParams: { numericFormat: 'length-mm-2-deci' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
					valueGetter: params => {
						if (!params.data || params.data.form !== '04' || !params.data.producedLength) {
							return '';
						}
						return Number(params.data.producedLength) / 1000;
					}
				})
			],
			[
				'steelgradeDescription',
				new CommonColDef<Batch>({
					colId: 'steelgradeDescription',
					filter: DatacenterTextFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('order.steelgrade'),
					headerComponent: CheckboxHeaderComponent,
					field: 'steelgradeDescription',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'supplierOrderNumber',
				new CommonColDef<Batch>({
					colId: 'supplierOrderNumber',
					fields: ['supplierOrderNumber', 'supplierOrderItemNumber'],
					filter: DatacenterOrderNumberFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					columnType: ColumnType.ORDER_NUMBER_COLUMN,
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('filter.supplierOrderNumber'),
					suppressHeaderMenuButton: false,
					valueGetter: (params: ValueGetterParams<Batch>) =>
						`${ params.data?.supplierOrderNumber ?? '' }/${ params.data?.supplierOrderItemNumber ?? '' }`
				})
			],
			[
				'thickness',
				new CommonColDef<Batch>({
					colId: 'thickness',
					filter: DatacenterNumberFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('filter.thickness'),
					headerComponent: CheckboxHeaderComponent,
					field: 'thickness',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			],
			[
				'width',
				new CommonColDef<Batch>({
					colId: 'width',
					filter: DatacenterNumberFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-shipment.width'),
					headerComponent: CheckboxHeaderComponent,
					field: 'width',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN
				})
			]
		]);
	}
}

export const DEFAULT_VIEW_ECALL_BY_BATCH_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'batchReference',
			pinned: 'left'
		},
		{
			colId: 'customerArticleReference'
		},
		{
			colId: 'customerOrderNumber'
		},
		{
			colId: 'millDescription'
		},
		{
			colId: 'steelgradeDescription'
		},
		{
			colId: 'thickness'
		},
		{
			colId: 'width'
		},
		{
			colId: 'currentLocation'
		},
		{
			colId: 'qtyBatchNet'
		},
		{
			colId: 'qtyBatchGross'
		},
		{
			colId: 'daysInStock'
		},
		{
			colId: 'serviceLevel'
		},
		{
			colId: 'cdd'
		}
	],
	sortModel: [
		{
			colId: 'cdd',
			sort: 'asc'
		},
		{
			colId: 'daysInStock',
			sort: 'desc'
		}
	],
	filterState: [],
	type: ECALL_BY_BATCH_DATACENTER_TYPE,
	isDefault: true,
	name: 'Default',
	id: -99
};
